import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Loader2, Filter, AlertCircle, Sparkles, GitFork } from 'lucide-react';
import ModePopover, { ModeTrigger } from './ModePopover';
import { useDebounce } from '../../hooks/useDebounce';
import * as api from '../../services/api';

export default function SearchHeader({
  value = '',
  modeValue = 'auto',
  loading,
  onSearch,
  activeFilterCount = 0,
  hasPendingFilterChanges = false,
  onOpenFilters,
  filtersOpen,
  suggestion,
  phrasingAlternatives = [],
}) {
  const [input, setInput] = useState(value || '');
  const [mode, setMode] = useState(modeValue || 'auto');
  const [modeOpen, setModeOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const debouncedInput = useDebounce(input, 300);

  useEffect(() => { setInput(value || ''); }, [value]);
  useEffect(() => { setMode(modeValue || 'auto'); }, [modeValue]);

  // Autocomplete for HS-code prefixes
  useEffect(() => {
    if (!debouncedInput || debouncedInput.length < 2) {
      setSuggestions([]);
      return;
    }
    if (/^\d{2,8}$/.test(debouncedInput.trim())) {
      api.suggestCodes(debouncedInput.trim(), 8)
        .then(data => {
          setSuggestions(data || []);
          setShowSuggestions(Boolean(data && data.length > 0));
        })
        .catch(() => setSuggestions([]));
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [debouncedInput]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (formRef.current && !formRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (input.trim()) {
      setShowSuggestions(false);
      onSearch(input.trim(), mode);
    }
  };

  const handleSuggestionClick = (code) => {
    setInput(code);
    setShowSuggestions(false);
    onSearch(code, mode);
  };

  const handleClear = () => {
    setInput('');
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const handleModeChange = (next) => {
    setMode(next);
    if (input.trim()) onSearch(input.trim(), next);
  };

  const showPercentHint = input.includes('%') && mode === 'auto';

  return (
    <div className="flex-shrink-0 bg-surface border-b border-border-default relative isolate z-30">
      <div className="max-w-[1920px] w-full mx-auto px-4 py-2.5">
        <form ref={formRef} onSubmit={handleSubmit} className="flex items-center gap-2">
          {/* Single bar: input + clear + mode + submit */}
          <div className="relative flex-1 min-w-0">
            <div className="flex h-11 items-center gap-2 rounded-xl border border-border-default bg-elevated pl-3 pr-1.5 transition-shadow focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 dark:focus-within:border-blue-400 dark:focus-within:ring-blue-400/20">
              <Search className="w-[18px] h-[18px] text-text-muted shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Код товара, артикул, описание или бренд..."
                className="h-full flex-1 min-w-0 bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none"
              />
              {input && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-hover hover:text-text-secondary transition-colors"
                  aria-label="Очистить"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <div className="relative h-7 border-l border-border-default pl-1.5 shrink-0">
                <ModeTrigger
                  mode={mode}
                  open={modeOpen}
                  onToggle={() => setModeOpen(o => !o)}
                />
                <ModePopover
                  open={modeOpen}
                  onClose={() => setModeOpen(false)}
                  mode={mode}
                  onChange={handleModeChange}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="ml-1 inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-500 dark:hover:bg-blue-400 transition-colors whitespace-nowrap"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Найти
              </button>
            </div>

            {/* Autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-elevated border border-border-default rounded-xl shadow-lg z-50 max-h-64 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSuggestionClick(s.code)}
                    className="w-full text-left px-4 py-2.5 hover:bg-hover transition-colors flex items-center justify-between"
                  >
                    <div className="min-w-0">
                      <span className="font-mono font-semibold text-blue-700 dark:text-blue-300">{s.code}</span>
                      {s.example_description && (
                        <span className="ml-2 text-text-muted text-sm truncate">
                          {s.example_description.slice(0, 80)}…
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-text-disabled ml-2 whitespace-nowrap">
                      {s.count.toLocaleString('ru-RU')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filters toggle */}
          <button
            type="button"
            onClick={onOpenFilters}
            aria-pressed={filtersOpen}
            className={`relative inline-flex h-11 items-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
              filtersOpen
                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50'
                : 'border-border-default bg-elevated text-text-secondary hover:bg-hover'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Фильтры</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold text-white">
                {activeFilterCount}
              </span>
            )}
            {hasPendingFilterChanges && (
              <span
                className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-surface"
                title="Фильтры изменены, нажмите чтобы применить"
              />
            )}
          </button>
        </form>

        {/* Suggestion ("Возможно, вы имели в виду") */}
        {suggestion && !loading && (
          <div className="mt-1.5 text-xs text-text-muted">
            Возможно, вы имели в виду:{' '}
            <button
              onClick={() => onSearch(suggestion, mode)}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium hover:underline cursor-pointer"
            >
              {suggestion}
            </button>
            ?
          </div>
        )}

        {/* Phrasing alternatives — canonical-vocabulary chips */}
        {!loading && phrasingAlternatives.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {phrasingAlternatives.map((p, i) => {
              const isRefine = p.kind === 'refine';
              const Icon = isRefine ? Sparkles : GitFork;
              const label = isRefine
                ? 'Точнее по таможенному корпусу'
                : 'Другая интерпретация';
              const chipClass = isRefine
                ? 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-900/40'
                : 'border-border-default bg-elevated text-text-secondary hover:bg-hover';
              return (
                <button
                  key={`${p.kind}-${i}`}
                  type="button"
                  onClick={() => onSearch(p.text, mode)}
                  title={p.reason || label}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${chipClass}`}
                >
                  <Icon className="w-3 h-3 shrink-0" />
                  <span className="text-text-muted/80 mr-0.5">{label}:</span>
                  <span>{p.text}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* % wildcard hint */}
        {showPercentHint && (
          <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 px-2 py-0.5 rounded-md">
            <AlertCircle className="w-3 h-3" />
            % → подстрока (ILIKE) при «Авто»
          </div>
        )}
      </div>
    </div>
  );
}
